// @ts-nocheck
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import { Attachment } from '../types';
import { formatTime, handleSliderPointerDown } from '../utils/helpers';
import { requestMicrophonePermission, checkSecureContext, VOICE_AUDIO_CONSTRAINTS } from '../utils/permissionUtils';
import { useAuth, useAudioPlayerState, useAudioPlayerProgress } from '../contexts/AuthContext';
import { incrementPlayCountOnServer } from '../api';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import {
    PlayIcon,
    PauseIcon,
    VolumeOffIcon,
    VolumeHighIcon,
    DownloadIcon,
    AudioIcon,
    MicIcon,
    TrashIcon
} from './Icons';

export interface CustomAudioPlayerHandle {
    togglePlayPause: () => void;
}

export const CustomAudioPlayer = forwardRef<CustomAudioPlayerHandle, { src: string, name?: string, autoplay?: boolean, post?: any, forcedDuration?: number, authorId?: string }>(({ src, name, autoplay = false, post, forcedDuration, authorId }, ref) => {
    const { usersData, currentUser } = useAuth() || {};
    const {
        activeAudioPost,
        playAudioPost,
        pauseAudioPost,
        resumeAudioPost,
        seekAudioPost,
        toggleMuteAudio,
        audioIsMuted,
        globalAnalyserRef,
        ensureGlobalAnalyser,
        audioIsPlaying
    } = useAudioPlayerState() || {};
    const {
        audioCurrentTime,
        audioDuration
    } = useAudioPlayerProgress() || {};

    // Resolve author styling — prefer post.author, then explicit authorId, then current user
    const resolvedAuthorId = post?.author || authorId || currentUser;
    const authorData = usersData?.[resolvedAuthorId] || {};
    const themeColor = authorData?.appThemeColor || '#3b82f6';
    const textColor = authorData?.appTextColor || '#ffffff';
    const rawOpacity = authorData?.postBlurOpacity !== undefined ? authorData.postBlurOpacity : 50;
    const bgOpacity = rawOpacity / 100;

    // Helper: hex → rgb string for rgba()
    const hexToRgb = (hex: string): string => {
        const h = (hex || '#000').replace('#', '');
        const full = h.length === 3 ? h[0]+h[0]+h[1]+h[1]+h[2]+h[2] : h;
        const r = parseInt(full.substring(0, 2), 16);
        const g = parseInt(full.substring(2, 4), 16);
        const b = parseInt(full.substring(4, 6), 16);
        return isNaN(r) ? '0,0,0' : `${r},${g},${b}`;
    };
    const themeRgb = hexToRgb(themeColor);
    const containerBg = `rgba(${themeRgb}, ${Math.max(0.15, bgOpacity * 0.4)})`;
    const containerBorder = `rgba(${themeRgb}, ${Math.max(0.2, bgOpacity * 0.5)})`;

    // Check if this source matches the active global audio source
    const media = (activeAudioPost?.type === 'bump' || activeAudioPost?.type === 'repost') 
        ? activeAudioPost?.originalPost?.content.media 
        : activeAudioPost?.content.media;
    const firstAudioUrl = media?.find((m: any) => m.type === 'audio' || m.type === 'voice')?.url;
    const isGlobalAudio = (post?.id && activeAudioPost?.id !== undefined && String(activeAudioPost.id) === String(post.id)) || (firstAudioUrl !== undefined && firstAudioUrl === src);

    const audioRef = useRef<HTMLAudioElement>(null);
    const [localIsPlaying, setLocalIsPlaying] = useState(false);
    const [localCurrentTime, setLocalCurrentTime] = useState(0);
    const [localDuration, setLocalDuration] = useState(forcedDuration || 0);
    const [localIsMuted, setLocalIsMuted] = useState(false);
    const [localIsLoading, setLocalIsLoading] = useState(!forcedDuration);
    const [localHasError, setLocalHasError] = useState<string | false>(false);
    const [volume, setVolume] = useState(1);
    const hasCountedPlayRef = useRef<boolean>(false);

    // Real-time playback visualizer via Web Audio API
    // 24 bars: halves FFT work vs 40 with negligible visual difference
    const VIZ_BAR_COUNT = 24;
    // Target ~12fps for visualizer — no perceptible diff vs 60fps for bar animations, uses ~80% less CPU
    const VIZ_FRAME_MS = 83;
    const [liveFreqBars, setLiveFreqBars] = useState<number[]>(new Array(VIZ_BAR_COUNT).fill(0));
    const playbackAudioCtxRef = useRef<AudioContext | null>(null);
    const playbackAnalyserRef = useRef<AnalyserNode | null>(null);
    const playbackSourceRef = useRef<MediaElementAudioSourceNode | null>(null);
    const playbackAnimRef = useRef<number | null>(null);
    const playbackLastFrameRef = useRef<number>(0);
    const globalAnimRef = useRef<number | null>(null); // RAF for global analyser polling
    const globalLastFrameRef = useRef<number>(0);
    const hasAutoplayedRef = useRef<boolean>(false);

    // Visualizer style — persisted per-post using src hash
    type VizStyle = 'bars' | 'wave' | 'mirror' | 'dots' | 'peaks';
    const VIZ_STYLES: VizStyle[] = ['bars', 'wave', 'mirror', 'dots', 'peaks'];
    const VIZ_LABELS: Record<VizStyle, string> = { bars: 'Bars', wave: 'Wave', mirror: 'Mirror', dots: 'Dots', peaks: 'Peaks' };
    const [vizStyle, setVizStyle] = useState<VizStyle>(() => {
        try {
            return (localStorage.getItem('vizStyle') as VizStyle) || 'bars';
        } catch { return 'bars'; }
    });
    const cycleVizStyle = () => {
        setVizStyle(prev => {
            const next = VIZ_STYLES[(VIZ_STYLES.indexOf(prev) + 1) % VIZ_STYLES.length];
            try { localStorage.setItem('vizStyle', next); } catch {}
            return next;
        });
    };

    // Static seeded waveform — only shown when paused (background pattern)
    const staticWaveform = useMemo(() => {
        let hash = 0;
        const source = src || '';
        for (let i = 0; i < source.length; i++) {
            hash = source.charCodeAt(i) + ((hash << 5) - hash);
        }
        return Array.from({ length: VIZ_BAR_COUNT }, (_, i) => Math.abs(Math.sin(hash * (i + 1))) * 0.55 + 0.15);
    }, [src]);

    const startPlaybackVisualizer = (audioEl: HTMLAudioElement) => {
        if (playbackAnimRef.current) return;
        let t = 0;
        const tick = () => {
            t += 0.2;
            let hash = 0;
            const source = src || '';
            for (let i = 0; i < source.length; i++) hash = source.charCodeAt(i) + ((hash << 5) - hash);
            
            const bars = Array.from({ length: VIZ_BAR_COUNT }, (_, i) => {
                const base = Math.abs(Math.sin(hash * (i + 1))) * 0.4 + 0.1;
                const anim = Math.sin(t + i * 0.5) * 0.2;
                return Math.max(0.1, base + anim);
            });
            setLiveFreqBars(bars);
            playbackAnimRef.current = requestAnimationFrame(tick);
        };
        playbackAnimRef.current = requestAnimationFrame(tick);
    };

    const stopPlaybackVisualizer = () => {
        if (playbackAnimRef.current) { cancelAnimationFrame(playbackAnimRef.current); playbackAnimRef.current = null; }
        setLiveFreqBars(new Array(VIZ_BAR_COUNT).fill(0));
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopPlaybackVisualizer();
        };
    }, []);

    // Sync forcedDuration into local state
    useEffect(() => {
        if (forcedDuration) {
            setLocalDuration(forcedDuration);
            setLocalIsLoading(false);
        }
    }, [forcedDuration]);

    // Bind state to global or local
    const isPlaying = isGlobalAudio ? audioIsPlaying : localIsPlaying;
    const currentTime = isGlobalAudio ? audioCurrentTime : localCurrentTime;
    const duration = isGlobalAudio ? audioDuration : localDuration;
    const isMuted = isGlobalAudio ? audioIsMuted : localIsMuted;
    const isLoading = isGlobalAudio ? false : localIsLoading;
    const hasError = isGlobalAudio ? false : localHasError;

    // Which bars to render — live when playing AND we have real data, otherwise static
    const hasLiveData = liveFreqBars.some(v => v > 0.1);
    const visualizerBars = (isPlaying && hasLiveData) ? liveFreqBars : staticWaveform;

    // Poll the global analyser for real-time frequency data when this is the global audio player
    useEffect(() => {
        if (!isGlobalAudio || !isPlaying) {
            if (globalAnimRef.current) {
                cancelAnimationFrame(globalAnimRef.current);
                globalAnimRef.current = null;
            }
            if (!isPlaying) setLiveFreqBars(new Array(VIZ_BAR_COUNT).fill(0));
            return;
        }
        // Ensure the analyser is connected before polling
        if (ensureGlobalAnalyser) ensureGlobalAnalyser();
        globalLastFrameRef.current = 0;

        const poll = (ts: number) => {
            // Throttle to ~12fps — cuts React state updates from ~60/sec to ~12/sec
            if (ts - globalLastFrameRef.current < VIZ_FRAME_MS) {
                globalAnimRef.current = requestAnimationFrame(poll);
                return;
            }
            globalLastFrameRef.current = ts;
            const analyser = globalAnalyserRef?.current;
            if (!analyser) {
                globalAnimRef.current = requestAnimationFrame(poll);
                return;
            }
            const data = new Uint8Array(analyser.frequencyBinCount);
            analyser.getByteFrequencyData(data);
            const bars: number[] = [];
            const step = Math.max(1, Math.floor(data.length / VIZ_BAR_COUNT));
            for (let i = 0; i < VIZ_BAR_COUNT; i++) {
                let sum = 0;
                for (let j = 0; j < step; j++) sum += data[i * step + j] || 0;
                bars.push(Math.max(0.04, (sum / step) / 255));
            }
            setLiveFreqBars(bars);
            globalAnimRef.current = requestAnimationFrame(poll);
        };
        globalAnimRef.current = requestAnimationFrame(poll);

        return () => {
            if (globalAnimRef.current) {
                cancelAnimationFrame(globalAnimRef.current);
                globalAnimRef.current = null;
            }
        };
    }, [isGlobalAudio, isPlaying, globalAnalyserRef, ensureGlobalAnalyser]);

    const togglePlayPause = () => {
        if (post && post.id && !hasCountedPlayRef.current) {
            hasCountedPlayRef.current = true;
            incrementPlayCountOnServer(post.id).catch(console.error);
        }

        if (isGlobalAudio && activeAudioPost && (!post || String(activeAudioPost.id) === String(post.id))) {
            if (audioIsPlaying) {
                if (pauseAudioPost) pauseAudioPost();
            } else {
                if (resumeAudioPost) resumeAudioPost();
            }
        } else if (post) {
            playAudioPost(post);
        } else {
            const audio = audioRef.current;
            if (!audio || hasError) return;
            if (localIsPlaying) {
                audio.pause();
                setLocalIsPlaying(false);
                stopPlaybackVisualizer();
            } else {
                if (playbackAudioCtxRef.current?.state === 'suspended') {
                    playbackAudioCtxRef.current.resume();
                }
                audio.play().then(() => {
                    setLocalIsPlaying(true);
                    setLocalHasError(false);
                    startPlaybackVisualizer(audio);
                }).catch(err => {
                    console.error("Playback failed:", err);
                    setLocalHasError(err.message || "Play Error");
                });
            }
        }
    };

    useImperativeHandle(ref, () => ({
        togglePlayPause
    }));

    const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
        const time = Number(e.target.value);
        if (isGlobalAudio) {
            seekAudioPost(time);
        } else {
            const audio = audioRef.current;
            if (!audio || hasError) return;
            audio.currentTime = time;
            setLocalCurrentTime(audio.currentTime);
        }
    };

    const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isGlobalAudio) return;
        const audio = audioRef.current;
        if (!audio) return;
        const newVolume = Number(e.target.value);
        audio.volume = newVolume;
        setVolume(newVolume);
        if (newVolume > 0 && localIsMuted) {
            setLocalIsMuted(false);
            audio.muted = false;
        }
        if (newVolume === 0 && !localIsMuted) {
            setLocalIsMuted(true);
            audio.muted = true;
        }
    };

    const toggleMute = () => {
        if (isGlobalAudio) {
            toggleMuteAudio();
        } else {
            const audio = audioRef.current;
            if (!audio) return;
            audio.muted = !localIsMuted;
            setLocalIsMuted(!localIsMuted);
        }
    };

    const handleAudioDataLoaded = () => {
        if (isGlobalAudio) return;
        const audio = audioRef.current;
        if (!audio) return;
        
        const setDurationSafely = () => {
            if (forcedDuration) {
                setLocalDuration(forcedDuration);
                return;
            }
            if (!isNaN(audio.duration) && audio.duration !== Infinity) {
                setLocalDuration(audio.duration);
            } else {
                // Hack for Chrome webm duration bug: 
                // Setting currentTime to a huge number forces the browser to calculate the true duration
                audio.currentTime = 1e10; 
                setTimeout(() => {
                     if (!isNaN(audio.duration) && audio.duration !== Infinity) {
                         setLocalDuration(audio.duration);
                     }
                     audio.currentTime = 0;
                }, 100);
            }
        };

        setDurationSafely();
        setLocalIsLoading(false); // Always unlock UI if metadata is loaded
        setLocalCurrentTime(audio.currentTime);

        if (autoplay) {
            audio.play().then(() => {
                setLocalIsPlaying(true);
                startPlaybackVisualizer(audio);
            }).catch(error => {
                    console.log("Autoplay was prevented:", error);
                    setLocalIsPlaying(false);
                });
        }
    };

    const handleTimeUpdate = () => {
        if (isGlobalAudio) return;
        const audio = audioRef.current;
        if (audio) {
            setLocalCurrentTime(audio.currentTime);
            if (forcedDuration) return;
            // Chromium natively sets duration to Infinity for blob webm files until the file is fully parsed or completely played through.
            // We need to continuously check if it has finally populated, OR if it's the end of the file.
            if (audio.duration !== Infinity && !isNaN(audio.duration) && audio.duration !== localDuration) {
                setLocalDuration(audio.duration);
            } else if (audio.currentTime > 0 && audio.duration === Infinity) {
                // If it's playing but duration is still Infinity, it's a webm blob. 
                // We'll update the duration as it plays, letting the visualizer expand as it goes.
                // It will fully set when ended.
                setLocalDuration(Math.max(localDuration, audio.currentTime));
            }
        }
    };

    const handleError = (e: any) => {
        if (!isGlobalAudio) {
            const err = e.target?.error;
            // Ignore abort errors which happen during rapid src changes
            if (err?.code === 1) return;
            console.error("Audio element error:", err);
            setLocalHasError(err ? `Err ${err.code}` : "Load Error");
            setLocalIsLoading(false);
        }
    };

    useEffect(() => {
        if (isGlobalAudio) return;
        // Reset state when src changes
        setLocalIsLoading(!forcedDuration);
        setLocalHasError(false);
        setLocalIsPlaying(false);
        setLocalCurrentTime(0);
        hasAutoplayedRef.current = false;
    }, [src, isGlobalAudio, forcedDuration]);

    useEffect(() => {
        if (forcedDuration) {
            setLocalDuration(forcedDuration);
        }
    }, [forcedDuration]);

    useEffect(() => {
        if (autoplay && !hasAutoplayedRef.current) {
            hasAutoplayedRef.current = true;

            let cleanupListeners = () => {};

            if (!post) {
                // Local audio playback lifecycle path
                const audio = audioRef.current;
                if (audio) {
                    const startOnInteraction = () => {
                        if (playbackAudioCtxRef.current?.state === 'suspended') {
                            playbackAudioCtxRef.current.resume();
                        }
                        audio.play().then(() => {
                            setLocalIsPlaying(true);
                            setLocalHasError(false);
                            startPlaybackVisualizer(audio);
                        }).catch(e => console.log("Interaction autoplay blocked:", e));
                        cleanupListeners();
                    };

                    cleanupListeners = () => {
                        document.removeEventListener('click', startOnInteraction);
                        document.removeEventListener('touchstart', startOnInteraction);
                    };

                    if (playbackAudioCtxRef.current?.state === 'suspended') {
                        playbackAudioCtxRef.current.resume();
                    }
                    audio.play().then(() => {
                        setLocalIsPlaying(true);
                        setLocalHasError(false);
                        startPlaybackVisualizer(audio);
                    }).catch(() => {
                        document.addEventListener('click', startOnInteraction);
                        document.addEventListener('touchstart', startOnInteraction);
                    });
                }
            } else {
                // Global context audio player path
                if (playAudioPost) {
                    const startOnInteraction = () => {
                        if (isGlobalAudio && audioIsPlaying) {
                            // Already playing globally, do nothing
                        } else if (isGlobalAudio && resumeAudioPost) {
                            resumeAudioPost();
                        } else {
                            playAudioPost(post);
                        }
                        cleanupListeners();
                    };

                    cleanupListeners = () => {
                        document.removeEventListener('click', startOnInteraction);
                        document.removeEventListener('touchstart', startOnInteraction);
                    };

                    if (!(isGlobalAudio && audioIsPlaying)) {
                        playAudioPost(post);
                    }

                    if (!audioIsPlaying) {
                        document.addEventListener('click', startOnInteraction);
                        document.addEventListener('touchstart', startOnInteraction);
                    }
                }
            }

            return () => {
                cleanupListeners();
            };
        }
    }, [autoplay, src, isGlobalAudio, post, playAudioPost, audioIsPlaying, activeAudioPost]);

    return (
        <div
            className={`flex items-center gap-3 p-1.5 pr-4 rounded-full  w-full cursor-pointer transition-all shadow-lg`}
            style={{ backgroundColor: containerBg, borderColor: containerBorder }}
        >
            <audio 
                ref={audioRef} 
                src={src} 
                preload="metadata" 
                crossOrigin="anonymous"
                style={{ position: 'fixed', left: '-9999px', top: '-9999px', width: '1px', height: '1px', pointerEvents: 'none', opacity: 0, zIndex: -9999 }}
                tabIndex={-1}
                onLoadedMetadata={handleAudioDataLoaded}
                onLoadedData={handleAudioDataLoaded}
                onCanPlay={handleAudioDataLoaded}
                onCanPlayThrough={handleAudioDataLoaded}
                onTimeUpdate={handleTimeUpdate}
                onEnded={() => { setLocalIsPlaying(false); stopPlaybackVisualizer(); }}
                onError={handleError}
            />

            <button
                type="button"
                onClick={(e) => { e.stopPropagation(); togglePlayPause(); }}
                className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full hover:brightness-110 active:scale-95 transition-all shadow-md"
                style={{ backgroundColor: themeColor, color: textColor }}
                aria-label={isPlaying ? "Pause" : "Play"}
            >
                {isPlaying ? <PauseIcon className="w-5 h-5" /> : <PlayIcon className="w-5 h-5 ml-1" />}
            </button>

            <div className="flex flex-col flex-grow min-w-0 justify-center h-full">
                {name && !name.startsWith('voice-recording-') && (
                    <p className="text-[11px] font-bold uppercase tracking-wider truncate mb-1.5 leading-none" style={{ color: textColor, opacity: 0.8 }}>
                        {name}
                    </p>
                )}

                <div className="flex items-center gap-2 w-full">
                    {/* Real-time visualizer seekbar */}
                    <div
                        className="relative h-5 flex-grow flex items-center group cursor-pointer"
                        onClick={(e) => e.stopPropagation()}
                        onDoubleClick={(e) => { e.stopPropagation(); cycleVizStyle(); }}
                        title="Double-tap to change style"
                    >
                        <div className="flex items-end justify-between w-full h-full gap-[1px]">
                            {visualizerBars.map((height: number, i: number) => {
                                const progress = duration > 0 ? currentTime / duration : 0;
                                const isActive = progress > (i / visualizerBars.length);

                                // Wave: sine-shaped offset
                                const waveOffset = vizStyle === 'wave'
                                    ? Math.sin((i / visualizerBars.length) * Math.PI * 2) * 0.15
                                    : 0;

                                // Mirror: reflect around center
                                const mirrorHeight = vizStyle === 'mirror'
                                    ? visualizerBars[Math.abs(i - (visualizerBars.length - 1))] || height
                                    : height;
                                const finalHeight = Math.max(0.05, Math.min(1, (vizStyle === 'mirror' ? (height + mirrorHeight) / 2 : height) + waveOffset));

                                if (vizStyle === 'peaks') {
                                    // Thin tall spikes with gradient color based on height
                                    const peakColor = isActive
                                        ? `rgba(${hexToRgb(themeColor)}, ${0.5 + finalHeight * 0.5})`
                                        : `rgba(${hexToRgb(textColor)}, 0.15)`;
                                    return (
                                        <div key={i} className="flex-1 flex items-end justify-center" style={{ height: '100%' }}>
                                            <div
                                                className="transition-all duration-75"
                                                style={{
                                                    height: `${finalHeight * 100}%`,
                                                    width: '2px',
                                                    borderRadius: '1px',
                                                    background: isActive
                                                        ? `linear-gradient(to top, ${themeColor}, rgba(${hexToRgb(themeColor)}, 0.3))`
                                                        : peakColor,
                                                }}
                                            />
                                        </div>
                                    );
                                }

                                if (vizStyle === 'dots') {
                                    return (
                                        <div
                                            key={i}
                                            className="flex-1 flex flex-col-reverse items-center justify-start"
                                            style={{ height: '100%' }}
                                        >
                                            <div
                                                className="rounded-full transition-all duration-75"
                                                style={{
                                                    height: `${Math.max(2, finalHeight * 80)}%`,
                                                    width: '55%',
                                                    background: isActive
                                                        ? `linear-gradient(to top, ${themeColor}, rgba(${hexToRgb(themeColor)}, 0.4))`
                                                        : `rgba(${hexToRgb(textColor)}, 0.2)`
                                                }}
                                            />
                                        </div>
                                    );
                                }

                                if (vizStyle === 'mirror') {
                                    const halfH = finalHeight / 2;
                                    const mirrorColor = isActive ? themeColor : `rgba(${hexToRgb(textColor)}, 0.15)`;
                                    return (
                                        <div key={i} className="flex-1 flex flex-col items-center justify-center" style={{ height: '100%' }}>
                                            <div className="w-full rounded-t-full transition-all duration-75" style={{
                                                height: `${halfH * 100}%`,
                                                background: isActive
                                                    ? `linear-gradient(to bottom, ${themeColor}, rgba(${hexToRgb(themeColor)}, 0.2))`
                                                    : mirrorColor
                                            }} />
                                            <div className="w-full rounded-b-full transition-all duration-75 mt-[1px]" style={{
                                                height: `${halfH * 100}%`,
                                                background: isActive
                                                    ? `linear-gradient(to top, ${themeColor}, rgba(${hexToRgb(themeColor)}, 0.2))`
                                                    : mirrorColor
                                            }} />
                                        </div>
                                    );
                                }

                                if (vizStyle === 'wave') {
                                    // Wave: smooth gradient fill bars
                                    return (
                                        <div
                                            key={i}
                                            className="flex-1 rounded-t-full transition-all duration-75"
                                            style={{
                                                height: `${finalHeight * 100}%`,
                                                background: isActive
                                                    ? `linear-gradient(to top, ${themeColor}, rgba(${hexToRgb(themeColor)}, 0.3))`
                                                    : `rgba(${hexToRgb(textColor)}, 0.15)`
                                            }}
                                        />
                                    );
                                }

                                // Default: bars with height-based gradient
                                return (
                                    <div
                                        key={i}
                                        className="flex-1 rounded-full transition-all duration-75"
                                        style={{
                                            height: `${finalHeight * 100}%`,
                                            background: isActive
                                                ? `linear-gradient(to top, ${themeColor}, rgba(${hexToRgb(themeColor)}, 0.4))`
                                                : `rgba(${hexToRgb(textColor)}, 0.2)`
                                        }}
                                    />
                                );
                            })}
                        </div>
                        <input
                            type="range"
                            min="0"
                            max={duration || 0}
                            step="0.01"
                            value={currentTime}
                            onChange={handleSeek}
                            disabled={isLoading || !!hasError || duration === 0}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                    </div>
                    {/* Style cycler button */}
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); cycleVizStyle(); }}
                        className="flex-shrink-0 text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full opacity-40 hover:opacity-80 transition-opacity"
                        style={{ color: textColor, border: `1px solid rgba(${hexToRgb(textColor)}, 0.2)` }}
                        title="Change visualizer style"
                    >
                        {VIZ_LABELS[vizStyle]}
                    </button>
                </div>
            </div>

            <div className="flex flex-col items-end flex-shrink-0 min-w-[35px] justify-center ml-1 gap-0.5">
                {hasError ? (
                    <span className="text-[10px] font-mono font-medium text-red-400 truncate max-w-[60px]" title={hasError as string}>{hasError}</span>
                ) : (
                    <>
                        <span className="text-[10px] font-mono font-medium leading-none" style={{ color: textColor, opacity: 0.9 }}>{formatTime(currentTime)}</span>
                        <span className="text-[9px] font-mono font-medium leading-none" style={{ color: textColor, opacity: 0.4 }}>{isLoading ? '...' : formatTime(duration)}</span>
                    </>
                )}
            </div>

            {!isLoading && !hasError && (
                <button
                    onClick={async (e) => {
                        e.stopPropagation();
                        try {
                            const response = await fetch(src);
                            const blob = await response.blob();
                            if (Capacitor.isNativePlatform()) {
                                const reader = new FileReader();
                                reader.readAsDataURL(blob);
                                reader.onloadend = async () => {
                                    const base64data = reader.result as string;
                                    const base64 = base64data.split(',')[1];
                                    const savedFile = await Filesystem.writeFile({
                                        path: name || 'audio.mp3',
                                        data: base64,
                                        directory: Directory.Cache
                                    });
                                    await Share.share({
                                        title: 'Download Audio',
                                        url: savedFile.uri
                                    });
                                };
                            } else {
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = name || 'audio.mp3';
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                window.URL.revokeObjectURL(url);
                            }
                        } catch (err) {
                            console.error('Failed to download audio:', err);
                            window.open(src, '_blank');
                        }
                    }}
                    className="flex-shrink-0 transition-colors ml-1 p-1 hover:text-white"
                    style={{ color: `rgba(${hexToRgb(textColor)}, 0.4)` }}
                    title="Download audio"
                >
                    <DownloadIcon className="w-4 h-4" />
                </button>
            )}
        </div>
    );
});


export const ReplyMediaPreview = ({ media }: { media: Attachment }) => {
    const [duration, setDuration] = useState<number | null>(null);
    useEffect(() => {
        if (!media) return;
        if (media.type === 'audio' || media.type === 'voice') {
            const audio = new Audio(media.url);
            const onLoaded = () => {
                if (!isNaN(audio.duration)) setDuration(audio.duration);
            };
            audio.addEventListener('loadedmetadata', onLoaded);
            audio.addEventListener('error', () => setDuration(null));
            audio.preload = 'metadata';
            audio.load();
            return () => {
                audio.pause();
                audio.removeEventListener('loadedmetadata', onLoaded);
            };
        }
    }, [media]);

    if (media.type === 'image') {
        return <img src={media.url} alt="reply image" className="w-12 h-12 object-cover rounded-md" />;
    }
    if (media.type === 'video') {
        return <video src={`${media.url}#t=0.001`} className="w-12 h-12 object-cover rounded-md" muted playsInline preload="metadata" />;
    }
    if (media.type === 'audio' || media.type === 'voice') {
        return (
            <div className="flex items-center gap-2">
                {media.type === 'audio' ? <AudioIcon className="w-5 h-5" /> : <MicIcon className="w-5 h-5" />}
                <span className="text-sm text-gray-300">Audio {duration ? `(${formatTime(Math.round(duration))})` : ''}</span>
            </div>
        );
    }
    return null;
};

export const AudioDurationDisplay = ({ media }: { media: Attachment }) => {
    const [duration, setDuration] = useState<number | null>(null);
    useEffect(() => {
        if (!media) return;
        const audio = new Audio(media.url);
        const onLoaded = () => {
            if (!isNaN(audio.duration)) setDuration(audio.duration);
        };
        audio.addEventListener('loadedmetadata', onLoaded);
        audio.addEventListener('error', () => setDuration(null));
        audio.preload = 'metadata';
        audio.load();
        return () => {
            audio.pause();
            audio.removeEventListener('loadedmetadata', onLoaded);
        };
    }, [media]);
    return (
        <div className="flex items-center gap-2">
            {media.type === 'audio' ? <AudioIcon className="w-5 h-5" /> : <MicIcon className="w-5 h-5" />}
            <span className="text-sm text-gray-300">{duration ? `(${formatTime(Math.round(duration))})` : ''}</span>
        </div>
    );
};

export interface AudioRecorderHandle {
    stopRecording: () => void;
}

export const AudioRecorder = forwardRef<AudioRecorderHandle, {
    onRecordingComplete: (url: string, blob: Blob, durationSeconds: number) => void,
    onReset: () => void,
    autoStart?: boolean,
    showControls?: boolean,
    stream?: MediaStream | null
}>(({ onRecordingComplete, onReset, autoStart = false, showControls = true, stream }, ref) => {
    const [isRecording, setIsRecording] = useState(false);
    const [timer, setTimer] = useState(0);
    const [audioURL, setAudioURL] = useState('');
    const [micError, setMicError] = useState<string | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const timerIntervalRef = useRef<number | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const currentSessionRef = useRef<symbol | null>(null);
    const intentionalStopRef = useRef(false);
    const startTimeRef = useRef<number>(0);

    // Live visualizer state
    const [liveVolume, setLiveVolume] = useState<number[]>(new Array(20).fill(0.1));
    const liveAudioCtxRef = useRef<AudioContext | null>(null);
    const liveAnalyserRef = useRef<AnalyserNode | null>(null);
    const liveAnimRef = useRef<number | null>(null);
    const liveLastFrameRef = useRef<number>(0);

    const startRecording = async () => {
        try {
            const session = Symbol();
            currentSessionRef.current = session;
            intentionalStopRef.current = false;
            let activeStream = stream;
            if (!activeStream) {
                await requestMicrophonePermission();
                activeStream = await navigator.mediaDevices.getUserMedia({ audio: VOICE_AUDIO_CONSTRAINTS });
            }
            const streamToRecord = activeStream;
            localStorage.setItem('goodlyfe_perm_mic_granted', 'true');
            // Just let the browser pick the best native format (avoids strict codec mismatches on playback)
            mediaRecorderRef.current = new MediaRecorder(streamToRecord);
            audioChunksRef.current = [];

            mediaRecorderRef.current.addEventListener("dataavailable", event => {
                if (event.data && event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            });

            // Start live visualizer
            try {
                const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
                liveAudioCtxRef.current = new AudioContextClass();
                liveAnalyserRef.current = liveAudioCtxRef.current.createAnalyser();
                liveAnalyserRef.current.fftSize = 64;
                const sourceNode = liveAudioCtxRef.current.createMediaStreamSource(streamToRecord);
                sourceNode.connect(liveAnalyserRef.current);
                liveLastFrameRef.current = 0;

                const updateLiveVisualizer = (ts: number) => {
                    // Throttle to ~12fps
                    if (ts - liveLastFrameRef.current < 83) {
                        liveAnimRef.current = requestAnimationFrame(updateLiveVisualizer);
                        return;
                    }
                    liveLastFrameRef.current = ts;
                    if (liveAnalyserRef.current) {
                        const dataArray = new Uint8Array(liveAnalyserRef.current.frequencyBinCount);
                        liveAnalyserRef.current.getByteFrequencyData(dataArray);
                        const bars = [];
                        const step = Math.max(1, Math.floor(dataArray.length / 20));
                        for (let i = 0; i < 20; i++) {
                            let sum = 0;
                            for (let j = 0; j < step; j++) {
                                sum += dataArray[i * step + j];
                            }
                            bars.push(Math.max(0.05, (sum / step) / 255));
                        }
                        setLiveVolume(bars);
                    }
                    liveAnimRef.current = requestAnimationFrame(updateLiveVisualizer);
                };
                liveAnimRef.current = requestAnimationFrame(updateLiveVisualizer);
            } catch (e) {
                console.warn("Live visualizer init failed", e);
            }

            mediaRecorderRef.current.addEventListener("stop", () => {
                // Clean up visualizer
                if (liveAnimRef.current) cancelAnimationFrame(liveAnimRef.current);
                if (liveAudioCtxRef.current) liveAudioCtxRef.current.close();

                if (currentSessionRef.current !== session) {
                    return; // Session was cancelled or changed
                }
                
                if (audioChunksRef.current.length === 0) {
                    console.error("No audio chunks recorded.");
                    setMicError("Recording failed (0 bytes).");
                    return;
                }

                // Let the browser define the blob type natively
                const finalMimeType = audioChunksRef.current[0].type || 'audio/webm';
                const audioBlob = new Blob(audioChunksRef.current, { type: finalMimeType });
                const url = URL.createObjectURL(audioBlob);
                if (window.location) {
                     setAudioURL(url);
                }
                
                const durationSeconds = (Date.now() - startTimeRef.current) / 1000;
                onRecordingComplete(url, audioBlob, durationSeconds);
                streamToRecord?.getTracks().forEach(track => track.stop());
            });

            mediaRecorderRef.current.start(200);
            startTimeRef.current = Date.now();
            setIsRecording(true);
            setTimer(0);
            timerIntervalRef.current = window.setInterval(() => {
                setTimer(prevTime => prevTime + 1);
            }, 1000);
        } catch (err: any) {
            console.error("Error accessing microphone:", err);
            const secureCheck = checkSecureContext();
            setMicError(secureCheck.isSecure ? `Mic Error: ${err.name}` : secureCheck.message);
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
            intentionalStopRef.current = true;
            try {
                // Request any final data buffered
                mediaRecorderRef.current.requestData();
            } catch (e) {}
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
            setTimer(0);
        }
    };

    useImperativeHandle(ref, () => ({
        stopRecording
    }));

    useEffect(() => {
        if (autoStart && !audioURL) {
            startRecording();
        }
        return () => {
            if (!intentionalStopRef.current) {
                currentSessionRef.current = null;
            }
            if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
                mediaRecorderRef.current.stop();
            }
        };
    }, [autoStart, stream]);

    const resetRecording = () => {
        setAudioURL('');
        setTimer(0);
        setIsRecording(false);
        onReset();
    }

    return (
        <div className="bg-gray-800/50 p-4 rounded-lg flex items-center gap-4 mt-2">
            {showControls && !audioURL && (
                <button
                    type="button"
                    onClick={isRecording ? stopRecording : startRecording}
                    className={`flex items-center justify-center w-12 h-12 rounded-full transition-colors ${isRecording ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'}`}
                    aria-label={isRecording ? "Stop recording" : "Start recording"}
                >
                    <MicIcon className="w-6 h-6 text-white" />
                </button>
            )}
            <div className="flex-grow">
                {micError && <div className="text-sm font-mono text-red-400 truncate max-w-full" title={micError}>{micError}</div>}
                {isRecording && (
                    <div className="flex items-center justify-between w-full h-12 px-3 bg-black/30 rounded-2xl border border-blue-500/30">
                        <div className="flex items-end justify-start h-6 gap-1 w-32">
                            {liveVolume.map((vol, i) => (
                                <div 
                                    key={i} 
                                    className="flex-1 bg-blue-500 rounded-t-sm transition-all duration-75"
                                    style={{ height: `${Math.max(5, vol * 100)}%` }}
                                />
                            ))}
                        </div>
                        <div className="text-sm font-mono text-blue-400 animate-pulse">{formatTime(timer)}</div>
                    </div>
                )}
                {!isRecording && !audioURL && !micError && <div className="text-gray-400 pl-2">Tap mic to record...</div>}
                {audioURL && (
                    <div className="flex items-center gap-2">
                        <div className="w-full">
                            <CustomAudioPlayer src={audioURL} />
                        </div>
                        <button type="button" onClick={resetRecording} aria-label="Delete recording">
                            <TrashIcon className="w-6 h-6 text-red-500 hover:text-red-400" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
});
